/**
 * AdminDashboardNew - Enterprise admin dashboard
 * Shows pipeline stats, client health, recent activity, quick actions
 * Fetches real data from API
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, navigate, getAdminData, hasPermission } from './api';

const STATUS_COLORS = {
  sent: '#5B9DFF', viewed: '#FFB347', accepted: '#00D4AA', expired: '#FF6B6B', draft: '#8B92A5',
  active: '#10B981', onboarding: '#3B82F6', churned: '#EF4444', suspended: '#F59E0B',
};

const TIER_COLORS = {
  smb: { bg: 'rgba(0,212,170,0.12)', text: '#00D4AA' },
  midmarket: { bg: 'rgba(59,130,246,0.12)', text: '#5B9DFF' },
  enterprise: { bg: 'rgba(192,132,252,0.12)', text: '#C084FC' },
};

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ backgroundColor: '#1A2332', borderRadius: 12, padding: 24, border: '1px solid #2A3444' }}>
      <div style={{ fontSize: 12, color: '#8B92A5', marginBottom: 6, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: color || '#FFF', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, marginTop: 8, color: '#5A6175' }}>{sub}</div>}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AdminDashboardNew() {
  const admin = getAdminData();
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      adminFetch('/api/admin/prospects').then(r => r.json()).catch(() => ({ prospects: [] })),
      hasPermission('clients', 'view')
        ? adminFetch('/api/admin/clients').then(r => r.json()).catch(() => ({ clients: [] }))
        : Promise.resolve({ clients: [] }),
    ]).then(([pData, cData]) => {
      setProspects(pData.prospects || []);
      setClients(cData.clients || []);
      setLoading(false);
    });
  }, []);

  const pStats = {
    total: prospects.length,
    viewed: prospects.filter(p => p.status === 'viewed').length,
    accepted: prospects.filter(p => p.status === 'accepted').length,
    pending: prospects.filter(p => p.status === 'sent').length,
  };

  const cStats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    onboarding: clients.filter(c => c.status === 'onboarding').length,
  };

  return (
    <AdminShell title={`Welcome back, ${admin?.name?.split(' ')[0] || 'Admin'}`}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#5A6175' }}>Loading dashboard...</div>
      ) : (
        <>
          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 32 }}>
            <StatCard label="Prospects" value={pStats.total} color="#FFF" sub={`${pStats.pending} pending`} />
            <StatCard label="Viewed" value={pStats.viewed} color="#FFB347" sub="Awaiting decision" />
            <StatCard label="Accepted" value={pStats.accepted} color="#00D4AA" sub={pStats.total > 0 ? `${Math.round((pStats.accepted / pStats.total) * 100)}% rate` : '-'} />
            <StatCard label="Active Clients" value={cStats.active} color="#10B981" sub={`${cStats.onboarding} onboarding`} />
            <StatCard label="Total Clients" value={cStats.total} color="#3B82F6" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Recent Prospects */}
            <div style={{ backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Recent Prospects</h3>
                <span style={{ fontSize: 12, color: '#3B82F6', cursor: 'pointer' }} onClick={() => navigate('/admin/prospects')}>View all</span>
              </div>
              {prospects.length === 0 ? (
                <div style={{ color: '#5A6175', fontSize: 13, padding: 20, textAlign: 'center' }}>No prospects yet</div>
              ) : (
                prospects.slice(0, 5).map((p, i) => (
                  <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 4 ? '1px solid #2A344433' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{p.company_name || p.companyName}</div>
                      <div style={{ fontSize: 12, color: '#5A6175' }}>{p.contact_name || p.contactName}</div>
                    </div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                      backgroundColor: `${STATUS_COLORS[p.status] || '#5A6175'}20`,
                      color: STATUS_COLORS[p.status] || '#5A6175',
                    }}>{p.status}</span>
                  </div>
                ))
              )}
            </div>

            {/* Recent Clients */}
            <div style={{ backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Active Clients</h3>
                {hasPermission('clients', 'view') && (
                  <span style={{ fontSize: 12, color: '#3B82F6', cursor: 'pointer' }} onClick={() => navigate('/admin/clients')}>View all</span>
                )}
              </div>
              {clients.length === 0 ? (
                <div style={{ color: '#5A6175', fontSize: 13, padding: 20, textAlign: 'center' }}>No clients yet</div>
              ) : (
                clients.slice(0, 5).map((c, i) => (
                  <div key={c.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 4 ? '1px solid #2A344433' : 'none' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{c.company_name}</div>
                      <div style={{ fontSize: 12, color: '#5A6175' }}>{c.tier || 'smb'}</div>
                    </div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                      backgroundColor: `${STATUS_COLORS[c.status] || '#5A6175'}20`,
                      color: STATUS_COLORS[c.status] || '#5A6175',
                    }}>{c.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ marginTop: 32, display: 'flex', gap: 12 }}>
            {hasPermission('prospects', 'create') && (
              <button onClick={() => navigate('/admin/prospects')} style={quickBtn}>+ New Prospect</button>
            )}
            {hasPermission('clients', 'create') && (
              <button onClick={() => navigate('/admin/clients')} style={quickBtn}>+ Add Client</button>
            )}
            {hasPermission('tickets', 'view') && (
              <button onClick={() => navigate('/admin/tickets')} style={{ ...quickBtn, background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>View Tickets</button>
            )}
          </div>
        </>
      )}
    </AdminShell>
  );
}

const quickBtn = {
  padding: '10px 20px',
  borderRadius: 8,
  border: 'none',
  background: 'linear-gradient(135deg, #0057FF, #0040CC)',
  color: '#FFF',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
